import { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { PiCheckCircle } from "react-icons/pi";

import useUiContext from "@/custolink-hooks/useUiContext";
import useFormServerBacked from "@/custolink-hooks/form/useFormServerBacked";
import useFormErrorPicker from "@/custolink-hooks/form/useFormErrorPicker";
import {
  PublicSharedUserApi,
  type PublicSharedUserApi$ResetPasswordInput,
} from "@/custolink-transpiled_by_server/bc.identity_and_access.api_methods";
import { normaliseHex } from "@/custolink-views/asset_frontend/redesigns/theme";
import { usePreviewFonts } from "@/custolink-views/asset_frontend/redesigns/fonts";
import { AssetShell, PreviewHeader } from "@/custolink-views/asset_frontend/redesigns/tileKit";
import {
  FormField,
  InfoNote,
  PasswordField,
  PrimaryButton,
} from "@/custolink-views/asset_frontend/redesigns/formKit";

/**
 * Reset password for an end user, asset frontend design.
 *
 * Behaviour is ResetPasswordSharedForm's, unchanged: same
 * PublicSharedUserApi.resetPassword call, same client-side repeat-password
 * check before a request is even made, same form.reset keyed off the token
 * param. This page does not reuse ResetPasswordSharedForm because that
 * component carries the classic Form/FormItem/PasswordInput markup - only
 * the presentation moves, onto tileKit.AssetShell / formKit.
 */
export default function ResetPasswordForEndUserDesign() {
  const { token } = useParams();
  const uic = useUiContext();
  const navigate = useNavigate();
  const [done, setDone] = useState(false);
  usePreviewFonts(["schibsted"]);

  const lf = uic.organisationContext.lookAndFeel;
  const accent = normaliseHex(lf.assetFrontendBgcolorHex);

  return (
    <AssetShell accent={accent}>
      <PreviewHeader logoUrl={lf.logoUrl} />

      <div className="flex flex-col gap-3 px-4">
        <div className="rounded-[10px] sm:rounded-2xl border border-[color:var(--rda-hairline)] bg-[var(--rda-surface)] px-4 py-4 sm:py-5 text-[var(--rda-on-surface)]">
          <h1 className="text-lg font-semibold tracking-[-0.01em] sm:text-xl">
            {done ? "Reset done" : "Reset your password"}
          </h1>
          <p className="mt-1 text-sm text-[var(--rda-muted)]">
            {done
              ? "Your password has been successfully reset."
              : "Choose a new password for your account."}
          </p>

          {done ? (
            <div className="mt-4 flex flex-col gap-3">
              <InfoNote>
                <span className="flex items-center gap-2">
                  <PiCheckCircle aria-hidden className="text-lg" />
                  Your password has been successfully reset.
                </span>
              </InfoNote>
              <PrimaryButton type="button" onClick={() => navigate("/")}>
                Continue
              </PrimaryButton>
            </div>
          ) : (
            <ResetForm token={token ?? ""} onDone={() => setDone(true)} />
          )}
        </div>
      </div>
    </AssetShell>
  );
}

function ResetForm(p: { token: string; onDone: () => void }) {
  const uic = useUiContext();
  const [repeatError, setRepeatError] = useState(false);

  // This page is entirely customer-facing, so the error toast follows the asset-frontend
  // switch, not the admin one.
  const form = useFormServerBacked<
    PublicSharedUserApi$ResetPasswordInput & { repeatPassword: string }
  >(
    {},
    (d) => {
      if (d.newPassword != d.repeatPassword) {
        if (d.newPassword.length > 0) {
          setRepeatError(true);
        }
        return Promise.reject();
      } else {
        setRepeatError(false);
        return PublicSharedUserApi.resetPassword(d);
      }
    },
    () => {
      p.onDone();
    },
    undefined,
    "asset",
  );

  useEffect(() => {
    form.reset({ organisationId: uic._orgId, newPassword: "", token: p.token });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.token]);

  const e = useFormErrorPicker(form.errors, "newPassword");

  return (
    <form noValidate className="mt-4 flex flex-col gap-1" onSubmit={form.handleSubmit()}>
      <FormField required label="New password" error={e.newPassword}>
        <Controller
          name="newPassword"
          control={form.control}
          render={({ field }) => (
            <PasswordField autoComplete="off" placeholder="••••••••••••" {...field} />
          )}
        />
      </FormField>

      <FormField
        required
        label="Repeat password"
        error={repeatError ? "Passwords do not match" : undefined}
      >
        <Controller
          name="repeatPassword"
          control={form.control}
          render={({ field }) => (
            <PasswordField autoComplete="off" placeholder="Confirm password" {...field} />
          )}
        />
      </FormField>

      <PrimaryButton
        className="mt-3"
        type="submit"
        disabled={!form.isDirty || form.isSubmitting}
      >
        {form.isSubmitting ? "Submitting..." : "Submit"}
      </PrimaryButton>
    </form>
  );
}
